import { useRef } from 'react';
import PropTypes from 'prop-types';

import styles from './styles.module.scss';

const FIGMA_W = 375;
const FIGMA_H = 812;

const FALLBACK_W = 281.25;
const FALLBACK_H = 609;

const INNER_BLOB_W = 302.64;
const INNER_BLOB_H = 262.44;
const INNER_BLOB_LEFT_OFFSET = -0.62;

const CORNER_GLOW_SIZE = 187;
const BORDER_RADIUS = 8;

const BORDER_LOOP_DURATION_MS = 3800;
const INNER_BLUR_LOOP_DURATION_S = 12;

const CORNERS = {
  topLeft: {
    cx: -44 + CORNER_GLOW_SIZE / 2,
    cy: -46 + CORNER_GLOW_SIZE / 2,
  },
  topRight: {
    cx: FIGMA_W - (-67) - CORNER_GLOW_SIZE / 2,
    cy: -60 + CORNER_GLOW_SIZE / 2,
  },
  bottomRight: {
    cx: FIGMA_W - (-68) - CORNER_GLOW_SIZE / 2,
    cy: FIGMA_H - (-62) - CORNER_GLOW_SIZE / 2,
  },
  bottomLeft: {
    cx: -46 + CORNER_GLOW_SIZE / 2,
    cy: FIGMA_H - (-48) - CORNER_GLOW_SIZE / 2,
  },
};

const scaleX = (value, w) => (value / FIGMA_W) * w;
const scaleY = (value, h) => (value / FIGMA_H) * h;

const GenerationOverlay = ({ width, height }) => {
  const w = width || FALLBACK_W;
  const h = height || FALLBACK_H;
  const isLandscape = w >= h;

  const uid = useRef(
    `go-${Math.random().toString(36)
      .slice(2, 8)}`
  ).current;

  const animBegin = useRef(
    `-${Math.floor(Math.random() * BORDER_LOOP_DURATION_MS)}ms`
  ).current;

  const innerBlurId = `inner-blur-${uid}`;
  const innerGradientId = `inner-gradient-${uid}`;
  const borderGlowBlurId = `border-glow-blur-${uid}`;
  const borderMaskId = `border-mask-${uid}`;

  const radius = Math.min(scaleX(BORDER_RADIUS, w), 8);

  const innerBlobWidth = isLandscape
    ? Math.max(scaleX(INNER_BLOB_W, w), w * 0.74)
    : scaleX(INNER_BLOB_W, w);
  const innerBlobHeight = isLandscape
    ? Math.max(scaleY(INNER_BLOB_H, h), h * 1.12)
    : scaleY(INNER_BLOB_H, h);
  const innerBlobX = (w - innerBlobWidth) / 2 + scaleX(INNER_BLOB_LEFT_OFFSET, w);

  const innerStartY = -innerBlobHeight * 0.55;
  const innerEndY = h - innerBlobHeight * 0.45;

  // Softer / larger blur field so no hard straight edge appears on big canvases
  const innerBlurStdDeviation = Math.max(scaleX(95, w), 55);

  const glowRadius = scaleX(CORNER_GLOW_SIZE, w) / 2;
  const borderGlowStdDeviation = Math.max(scaleX(18, w), 10);

  const trackInset = 0.375;
  const maskStroke = Math.max(scaleX(3.5, w), 2.4);

  const topLeft = {
    x: scaleX(CORNERS.topLeft.cx, w),
    y: scaleY(CORNERS.topLeft.cy, h),
  };
  const topRight = {
    x: scaleX(CORNERS.topRight.cx, w),
    y: scaleY(CORNERS.topRight.cy, h),
  };
  const bottomRight = {
    x: scaleX(CORNERS.bottomRight.cx, w),
    y: scaleY(CORNERS.bottomRight.cy, h),
  };
  const bottomLeft = {
    x: scaleX(CORNERS.bottomLeft.cx, w),
    y: scaleY(CORNERS.bottomLeft.cy, h),
  };

  // Distribute keyTimes proportionally so the glow travels at a consistent speed
  const segLen = (a, b) => Math.sqrt((b.x - a.x) ** 2 + (b.y - a.y) ** 2);
  const d1 = segLen(topLeft, topRight);
  const d2 = segLen(topRight, bottomRight);
  const d3 = segLen(bottomRight, bottomLeft);
  const d4 = segLen(bottomLeft, topLeft);
  const totalPerimeter = d1 + d2 + d3 + d4;

  const kt1 = d1 / totalPerimeter;
  const kt2 = (d1 + d2) / totalPerimeter;
  const kt3 = (d1 + d2 + d3) / totalPerimeter;
  const borderKeyTimes = `0; ${kt1.toFixed(4)}; ${kt2.toFixed(4)}; ${kt3.toFixed(4)}; 1`;

  const containerStyle
    = width && height
      ? { width: w, height: h }
      : { aspectRatio: `${FALLBACK_W} / ${FALLBACK_H}` };

  return (
    <div className={styles.container} style={containerStyle}>
      <div className={styles.base} />

      {/* Inner blur */}
      <svg
        className={styles.innerBlur}
        viewBox={`0 0 ${w} ${h}`}
        preserveAspectRatio="none"
        aria-hidden="true"
        focusable="false"
      >
        <defs>
          <filter
            id={innerBlurId}
            x="-220%"
            y="-220%"
            width="540%"
            height="540%"
            colorInterpolationFilters="sRGB"
          >
            <feGaussianBlur stdDeviation={innerBlurStdDeviation} />
          </filter>

          <linearGradient id={innerGradientId} x1="100%" y1="50%" x2="0%" y2="50%">
            <stop offset="0%" stopColor="rgba(152, 16, 250, 0.62)" />
            <stop offset="100%" stopColor="rgba(152, 16, 250, 0)" />
          </linearGradient>
        </defs>

        <g transform={`translate(${innerBlobX} 0)`}>
          <g filter={`url(#${innerBlurId})`}>
            <g>
              <animateTransform
                attributeName="transform"
                type="translate"
                values={`0 ${innerStartY}; 0 ${innerEndY}; 0 ${innerStartY}`}
                keyTimes="0; 0.5; 1"
                dur={`${INNER_BLUR_LOOP_DURATION_S}s`}
                repeatCount="indefinite"
                calcMode="linear"
              />

              <rect
                x={0}
                y={0}
                width={innerBlobWidth}
                height={innerBlobHeight}
                rx={innerBlobHeight / 2}
                ry={innerBlobHeight / 2}
                fill={`url(#${innerGradientId})`}
                opacity="0.5"
              />
            </g>
          </g>
        </g>
      </svg>

      <svg
        className={styles.borderSvg}
        viewBox={`0 0 ${w} ${h}`}
        preserveAspectRatio="none"
        aria-hidden="true"
        focusable="false"
      >
        <defs>
          <filter
            id={borderGlowBlurId}
            x="-180%"
            y="-180%"
            width="460%"
            height="460%"
            colorInterpolationFilters="sRGB"
          >
            <feGaussianBlur stdDeviation={borderGlowStdDeviation} />
          </filter>

          {/* Thin visible band so the glow looks like it runs on the border, not as a thick neon block */}
          <mask id={borderMaskId} maskUnits="userSpaceOnUse">
            <rect width={w} height={h} fill="black" />
            <rect
              x={trackInset}
              y={trackInset}
              width={w - trackInset * 2}
              height={h - trackInset * 2}
              rx={radius}
              fill="none"
              stroke="white"
              strokeWidth={maskStroke}
            />
          </mask>
        </defs>

        {/* Soft moving glow across corners */}
        <g mask={`url(#${borderMaskId})`}>
          <circle
            cx={topLeft.x}
            cy={topLeft.y}
            r={glowRadius}
            fill="rgba(173, 70, 255, 0.5)"
            filter={`url(#${borderGlowBlurId})`}
          >
            <animate
              attributeName="cx"
              values={`${topLeft.x}; ${topRight.x}; ${bottomRight.x}; ${bottomLeft.x}; ${topLeft.x}`}
              keyTimes={borderKeyTimes}
              dur={`${BORDER_LOOP_DURATION_MS}ms`}
              begin={animBegin}
              repeatCount="indefinite"
              calcMode="linear"
            />
            <animate
              attributeName="cy"
              values={`${topLeft.y}; ${topRight.y}; ${bottomRight.y}; ${bottomLeft.y}; ${topLeft.y}`}
              keyTimes={borderKeyTimes}
              dur={`${BORDER_LOOP_DURATION_MS}ms`}
              begin={animBegin}
              repeatCount="indefinite"
              calcMode="linear"
            />
          </circle>
        </g>
      </svg>

      <div className={styles.content}>
        <div className={styles.labelGroup}>
          <span
            className={styles.label}
            style={w < 160 ? { fontSize: Math.max(9, Math.round(w * 0.09)) } : undefined}
          >
            Generating...
          </span>
          <span
            className={styles.subLabel}
            style={w < 160 ? { fontSize: Math.max(8, Math.round(w * 0.08)) } : undefined}
          >
            up to 5 minutes
          </span>
        </div>
      </div>
    </div>
  );
};

GenerationOverlay.propTypes = {
  width: PropTypes.number,
  height: PropTypes.number,
};

GenerationOverlay.defaultProps = {
  width: null,
  height: null,
};

export default GenerationOverlay;
